import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Instagram, MapPin, Twitter, Sparkles, Star, Youtube, Hash, MessageCircle, Linkedin, Facebook, ShoppingCart, Users as Users2 } from 'lucide-react';
import { FilledIcon } from '../icons/FilledIcon';
import { BRAND_BLUE_LIGHT, gradientText, tint } from './brandAccent';

interface ScraperHighlight {
    id: string;
    name: string;
    platform: string;
    audience: string;
    rating: string;
    summary: string;
    bullets: string[];
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | 'filled';
    accent: string;
}

const scrapers: ScraperHighlight[] = [
    {
        id: 'instagram-scraper',
        name: '인스타그램 스크래퍼',
        platform: 'Instagram',
        audience: '69.3k',
        rating: '832',
        summary: '인스타그램 게시물·프로필·해시태그 데이터를 빠르게 수집하고, 마케팅 전략 수립에 필요한 인사이트를 제공합니다.',
        bullets: [
            '인플루언서 검증: 게시물과 팔로워 지표를 분석해 브랜드 적합도 파악',
            '경쟁사 벤치마크: 유사 계정의 해시태그 활용과 참여율을 비교',
            '콘텐츠 성과 추적: 해시태그별 도달과 참여 데이터를 정리'
        ],
        icon: Instagram,
        accent: '#4C9AFF'
    },
    {
        id: 'google-map-scraper',
        name: '구글 지도 추출기',
        platform: 'Google Maps',
        audience: '26.5k',
        rating: '629',
        summary: '매장 리뷰·별점·영업 정보를 구조화된 데이터로 추출해 로컬 비즈니스 인사이트를 강화합니다.',
        bullets: [
            '로컬 인텔리전스: 지역별 리뷰 동향과 별점 분포 파악',
            '입지 전략: 경쟁 매장 밀집도를 시각화해 신규 후보지 도출',
            '정보 유지보수: 반복 스케줄로 최신 매장 정보를 자동 갱신'
        ],
        icon: MapPin,
        accent: '#00D87E'
    },
    {
        id: 'youtube-scraper',
        name: '유튜브 스크래퍼',
        platform: 'YouTube',
        audience: '12.4k',
        rating: '373',
        summary: '채널 데이터와 동영상 성과를 정밀 분석하여 콘텐츠 전략 수립을 지원합니다.',
        bullets: [
            '채널 성과 분석: 구독자, 조회수, 좋아요 수를 분석하여 채널 성과 측정',
            '인기 영상 분석: 가장 인기 있는 영상들을 분석하여 콘텐츠 제작 방향 설정',
            '시장 동향 분석: 키워드나 주제 관련 영상들의 정보를 분석하여 시장 동향 파악'
        ],
        icon: Youtube,
        accent: '#FF0000'
    },
    {
        id: 'twitter-scraper',
        name: '트위터 스크래퍼',
        platform: 'Twitter',
        audience: '30.8k',
        rating: '122',
        summary: '실시간 트윗을 수집해 이슈별 여론과 감성 변화를 모니터링하고 위기 대응을 돕습니다.',
        bullets: [
            '여론 분석: 키워드별 언급량과 감성 비율을 모니터링',
            '트렌드 추적: 급증하는 해시태그를 감지해 캠페인 타이밍 최적화',
            '위기 대응: 부정 감성이 빠르게 증가할 때 조기 경보'
        ],
        icon: Twitter,
        accent: '#1DA1F2'
    },
    {
        id: 'tiktok-scraper',
        name: '틱톡 프로필 스크래퍼',
        platform: 'TikTok',
        audience: '6k',
        rating: '367',
        summary: '틱톡 프로필과 동영상 데이터를 완벽 분석하여 트렌드를 파악합니다.',
        bullets: [
            '인플루언서 마케팅: 프로필 정보를 수집하여 마케팅 전략 수립',
            '콘텐츠 성과 분석: 게시물 성과를 분석하여 효과적인 콘텐츠 전략 개발',
            '시장 조사: 주제나 트렌드에 대한 프로필 분석을 통해 시장 동향 파악'
        ],
        icon: Hash,
        accent: '#000000'
    },
    {
        id: 'linkedin-scraper',
        name: '링크드인 프로필 스크래퍼',
        platform: 'LinkedIn',
        audience: '926',
        rating: '62',
        summary: '전문가 프로필과 비즈니스 연락처 데이터를 수집하여 B2B 영업을 지원합니다.',
        bullets: [
            '잠재 고객 발굴: 프로필 정보를 사용하여 잠재 고객 발굴',
            '인재 채용: 잠재 채용 후보자를 발굴',
            '시장 조사: 특정 업계의 프로필 정보를 수집하여 시장 동향 파악'
        ],
        icon: Linkedin,
        accent: '#0A66C2'
    },
    {
        id: 'facebook-scraper',
        name: '페이스북 페이지 스크래퍼',
        platform: 'Facebook',
        audience: '20.3k',
        rating: '91',
        summary: '페이지 데이터와 광고 라이브러리를 분석하여 경쟁사 전략을 파악합니다.',
        bullets: [
            '경쟁사 페이지 분석: 페이지 정보를 분석하여 마케팅 전략 활용',
            '페이지 성과 측정: 좋아요 수, 팔로워 수 등을 분석하여 성과 측정',
            '광고 트렌드 분석: 광고 데이터 분석을 통해 광고 트렌드 파악'
        ],
        icon: Facebook,
        accent: '#1877F2'
    },
    {
        id: 'amazon-scraper',
        name: '아마존 스크래퍼',
        platform: 'Amazon',
        audience: '4.8k',
        rating: '81',
        summary: '제품 데이터와 리뷰를 분석하여 시장 동향을 파악하고 제품 전략을 수립합니다.',
        bullets: [
            '경쟁사 제품 분석: 가격, 리뷰, 설명을 분석하여 시장 동향 파악',
            '가격 변동 모니터링: 제품 가격 변동을 추적하여 최적 구매 시점 파악',
            '제품 리뷰 분석: 리뷰 데이터를 분석하여 고객 만족도 측정'
        ],
        icon: ShoppingCart,
        accent: '#FF9900'
    },
    {
        id: 'reddit-scraper',
        name: '레딧 스크래퍼',
        platform: 'Reddit',
        audience: '4.4k',
        rating: '76',
        summary: '커뮤니티 데이터를 분석하여 사용자 의견과 트렌드를 파악합니다.',
        bullets: [
            '커뮤니티 분석: 게시물, 댓글, 사용자 데이터를 분석하여 커뮤니티 분위기 파악',
            '여론 분석: 특정 주제에 대한 사용자 의견 파악',
            '트렌드 분석: 데이터를 분석하여 트렌드 파악'
        ],
        icon: MessageCircle,
        accent: '#FF4500'
    }
];

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index: number) => ({
        opacity: 1,
        y: 0,
        transition: { 
            duration: 0.5, 
            delay: index * 0.1,
            ease: "easeOut"
        }
    })
};

const DataScrapingShowcase: React.FC = () => {
    // 2026-11-03 디자인만 새로(대표 "스크래퍼 도구 찾는 것도 색상이 일관성 전혀 없어"):
    // 플랫폼마다 다른 색(아이콘·윗줄·글머리 점) → 회색 한 결, 강조는 '실시간' 초록 점 하나만. 빛 번짐·알약·글자 그라데이션·파란 단추 → 작은 머리글·회색 둘째 말·흰 캡슐.
    // 휴대폰은 카드 여덟 장을 세로로 쌓던 것을 좌우로 넘긴다. 글귀는 예전 그대로.
    return (
        <section className="relative overflow-hidden bg-black py-16 sm:py-24 lg:py-28">
            <div className="relative mx-auto flex w-full max-w-7xl flex-col gap-10 px-5 sm:gap-12 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                    className="flex flex-col items-start gap-4 text-left sm:gap-5"
                >
                    <span className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#A1A1AA]">
                        <Sparkles className="h-3.5 w-3.5 text-[#4C9AFF]" />
                        프리미엄 데이터 스크래핑
                    </span>
                    <div className="flex flex-col gap-3 sm:gap-4">
                        <h2 className="text-[30px] font-bold text-[#FAFAFA] sm:text-[44px] break-keep" style={{ lineHeight: 1.18, letterSpacing: '-0.03em', textWrap: 'balance' as never }}>
                            세계 최고 수준의{' '}
                            <span style={gradientText}>
                                데이터 스크래핑 도구
                            </span>
                            들을 한 곳에서
                        </h2>
                        <p className="max-w-2xl text-[16px] leading-relaxed text-[#A1A1AA] sm:text-[18px] break-keep">
                            Instagram, Google Maps, Twitter 등 다양한 플랫폼의 데이터를 손쉽게 추출하고 분석할 수 있습니다.
                            비즈니스 성장을 위한 강력한 인사이트를 발견하세요.
                        </p>
                    </div>
                    <Link
                        to="/data-scraping"
                        className="mt-1 inline-flex h-[54px] items-center justify-center rounded-full bg-white px-7 text-[15px] font-semibold text-[#0A0A0A] transition-colors hover:bg-white/90"
                    >
                        데이터 스크래핑 서비스 살펴보기
                    </Link>
                </motion.div>

                <div className="relative">
                    <div className="mb-5 flex items-center justify-between">
                        <h3 className="text-[18px] font-semibold text-[#FAFAFA] sm:text-[20px]">인기 스크래퍼 서비스</h3>
                        <Link
                            to="/data-scraping"
                            className="text-[13px] font-medium text-[#A1A1AA] transition-colors hover:text-[#FAFAFA]"
                        >
                            전체 보기 →
                        </Link>
                    </div>

                    {/* 휴대폰: 좌우로 넘김 / 태블릿·PC: 칸 */}
                    <div className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 lg:grid-cols-3 xl:grid-cols-4" style={{ scrollPaddingLeft: 20 }}>
                    {scrapers.slice(0, 8).map((scraper, index) => {
                        const IconComponent = scraper.icon !== 'filled' ? scraper.icon : null;

                        return (
                            <motion.div
                                key={scraper.id}
                                custom={index}
                                variants={cardVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.3 }}
                                className="w-[80%] shrink-0 snap-start sm:w-auto"
                            >
                                <div className="group relative flex h-full flex-col overflow-hidden rounded-[20px] border border-white/[0.08] bg-[#0F0F11] transition-colors hover:border-white/[0.16]">
                                    <div className="flex h-full flex-col gap-4 p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: tint(BRAND_BLUE_LIGHT) }}>
                                                {IconComponent ? (
                                                    <IconComponent className="h-[18px] w-[18px]" style={{ color: BRAND_BLUE_LIGHT }} />
                                                ) : (
                                                    <FilledIcon name="sparkles" size={18} className="text-[#4C9AFF]" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="truncate text-[15px] font-semibold text-[#FAFAFA]">
                                                    {scraper.name}
                                                </h3>
                                                <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#71717A]">
                                                    {scraper.platform}
                                                </span>
                                            </div>
                                        </div>

                                        <p className="line-clamp-2 text-[13px] leading-relaxed text-[#A1A1AA] break-keep">
                                            {scraper.summary}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-3 border-t border-white/[0.07] pt-3 text-[12px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                            <div className="flex items-center gap-1.5">
                                                <Users2 className="h-3.5 w-3.5 text-[#71717A]" />
                                                <span className="font-medium text-[#A1A1AA]">{scraper.audience}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <Star className="h-3.5 w-3.5 text-[#71717A]" />
                                                <span className="font-medium text-[#A1A1AA]">{scraper.rating}</span>
                                            </div>
                                            <div className="ml-auto flex items-center gap-1.5">
                                                <span className="h-1.5 w-1.5 rounded-full bg-[#34D399]" />
                                                <span className="font-medium text-[#A1A1AA]">실시간</span>
                                            </div>
                                        </div>

                                        <div className="hidden space-y-2 border-t border-white/[0.07] pt-3 sm:block">
                                            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#71717A]">활용 예시</span>
                                            <ul className="space-y-1.5">
                                                {scraper.bullets.slice(0, 2).map((bullet, bulletIndex) => {
                                                    const [title] = bullet.split(':');
                                                    return (
                                                        <li key={bulletIndex} className="flex items-start gap-2">
                                                            <span className="mt-[7px] h-[3px] w-[3px] flex-shrink-0 rounded-full bg-[#71717A]" />
                                                            <span className="text-[12px] leading-relaxed text-[#A1A1AA]">
                                                                {title}
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>

                                        <div className="mt-auto flex items-center justify-between pt-1">
                                            <Link
                                                to="/data-scraping"
                                                className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#FAFAFA] transition-colors hover:text-white"
                                            >
                                                더 알아보기
                                                <FilledIcon name="arrow-right" size={12} className="text-current" />
                                            </Link>
                                            <span className="text-[12px] text-[#71717A]">
                                                시작하기
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                    <div className="w-2 shrink-0 sm:hidden" aria-hidden />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DataScrapingShowcase;
